import type { RefCallback, CSSProperties } from 'react';
import { forwardRef, useEffect, useMemo } from 'react';
import type { Option } from '@unifyapps/defs/blocks/ButtonGroup/types';
import type { ToggleButtonGroupSizes } from '@unifyapps/ui/components/ToggleButtonGroup';
import BaseToggleButtonGroup from '@unifyapps/ui/components/ToggleButtonGroup';
import { Tooltip } from '@unifyapps/ui/components/Tooltip';
import { Typography } from '@unifyapps/ui/components/Typography';
import { getIconFromRegistry } from '@unifyapps/icons/utils/getIconFromRegistry';
import _map from 'lodash/map';
import { useTranslation } from '@unifyapps/i18n/client';
import useEventCallback from '@unifyapps/hooks/useEventCallback';
import { buttonGroupTextVariantMap } from '@unifyapps/ui/components/ToggleButtonGroup/components/ToggleGroupButtons/utils';
import { DefaultButtonGroup } from './DefaultButtonGroup';

type Props = {
  size?: ToggleButtonGroupSizes;
  className?: string;
  style?: CSSProperties;
  attributesAndEventListeners: Record<string, unknown> | undefined;
  options: Option[];
  onAction: (id: string, label: string) => void;
  selectedOptionId: string | undefined;
  dndRef?: RefCallback<HTMLElement | null> | null;
  variant?: 'solid' | 'outline';
  color?: 'neutral' | 'brand';
};

export const ToggleButtonGroup = forwardRef<HTMLDivElement, Props>(
  function ToggleButtonGroup(props, ref) {
    const {
      size = 'md',
      className,
      style,
      options,
      attributesAndEventListeners,
      onAction,
      selectedOptionId,
      variant = 'solid',
      color,
    } = props;
    const { t } = useTranslation(['common']);

    const items = useMemo(
      () =>
        options.map((option) => ({
          id: option.id,
          title: option.appearance?.loading ? `${t('common:Loading')}...` : option.label,
          tooltipContent: option.addOns?.tooltip ? option.addOns.tooltip.content : undefined,
          type: 'label' as const,
          disabled: option.appearance?.disabled,
          endDecoratorComponent: getIconFromRegistry(option.appearance?.endDecorator),
          startDecoratorComponent: getIconFromRegistry(option.appearance?.startDecorator),
        })),
      [options, t],
    );

    const handleChange = useEventCallback((event, value) => {
      const selectedOption = items.find((item) => item.id === value);
      if (selectedOption) {
        onAction(selectedOption.id, selectedOption.title);
      }
    });

    useEffect(() => {
      if (!selectedOptionId && items.length) {
        onAction(items[0]?.id, items[0]?.title);
      }
    }, [items, onAction, selectedOptionId]);

    if (variant === 'outline') {
      return (
        <DefaultButtonGroup
          {...props}
          color={color}
          selectedOptionId={selectedOptionId}
          variant="outline"
        />
      );
    }

    return (
      <BaseToggleButtonGroup
        className={className}
        color={color}
        onChange={handleChange}
        ref={ref}
        size={size}
        style={style}
        {...attributesAndEventListeners}
      >
        {_map(items, (item) => (
          <BaseToggleButtonGroup.Button
            className="grow whitespace-nowrap"
            color={color}
            disabled={item.disabled}
            endDecoratorComponent={item.endDecoratorComponent}
            key={item.id}
            selected={selectedOptionId === item.id}
            size={size}
            startDecoratorComponent={item.startDecoratorComponent}
            value={item.id}
          >
            {/* Adding Tooltip on Typography since it will not work button as it is disabled */}

            {item.tooltipContent ? (
              <Tooltip placement="top" title={item.tooltipContent}>
                <Typography
                  className="pointer-events-auto"
                  variant={buttonGroupTextVariantMap[size]}
                  weight="medium"
                >
                  {item.title}
                </Typography>
              </Tooltip>
            ) : (
              <Typography
                className="pointer-events-auto"
                variant={buttonGroupTextVariantMap[size]}
                weight="medium"
              >
                {item.title}
              </Typography>
            )}
          </BaseToggleButtonGroup.Button>
        ))}
      </BaseToggleButtonGroup>
    );
  },
);
